import { FC, useEffect, useState } from 'react';
import useRouteParams from '../../../../../hooks/useRouteParams';
import { useDispatch } from 'react-redux';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../../redux/ui/controlbar/actions';
import Raptor2Loading from '../../../../feedback/Raptor2Loading';
import { useNavigate } from 'react-router-dom';
import BackToOverviewBtn from '../../components/buttons/BackToOverviewBtn';
import { useRaidrKids } from '../context/RaidrKidsContext';
import { formatReviewContentFromResponse } from '../utils/review.utils';
import { useKIDsReviewData } from '../services/queries';
import { useGenerateNewReviewKIDPdf } from '../services/mutations';
import ErrorBoundaryMessage from '../../../../ui/ErrorBoundaryMessage';
import TokenService from '../../../../../services/token-service';
import { pdfjs } from 'react-pdf';
import { getOS } from '../../../../../utilities/helpers/common/system';
import RaptorPDFViewer from '../../../../general/raptorPDFViewer/RaptorPDFViewer';

const CompareKIDVersions: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user_client_id: clientId } = TokenService.getUser()!;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const OS = getOS();
  const [hasError, setHasError] = useState(false);

  const { iframeTitle, backToOverviewLink } = useRaidrKids();

  const [reviewContentData, setReviewContentData] = useState<any[]>([]);

  // Get route params
  const fundId = useRouteParams('fundId')['fundId'];
  const isin = useRouteParams('isin')['isin'];
  const documentLanguage =
    useRouteParams('documentLanguage')['documentLanguage'];
  const targetVersion = useRouteParams('targetVersion')['targetVersion'];
  const comparisonVersion =
    useRouteParams('comparisonVersion')['comparisonVersion'];

  const { data, isPending, error } = useKIDsReviewData(
    clientId,
    fundId,
    isin,
    targetVersion,
    comparisonVersion,
    documentLanguage,
  );

  const createNewReviewKIDPdf = useGenerateNewReviewKIDPdf();

  useEffect(() => {
    if (data) {
      setReviewContentData(formatReviewContentFromResponse(data));
    }
  }, [data]);

  const getPDF = async () => {
    // Check if the required fields have been filled in.
    if (!reviewContentData || !reviewContentData.length) {
      return;
    }

    try {
      // Create an object of formData
      const formData = new FormData();
      // Convert the content to json
      const kidsJson = JSON.stringify(reviewContentData);
      // Update the formData object.
      formData.append('contents', kidsJson);
      formData.append('params', JSON.stringify(['contents']));

      const response = await createNewReviewKIDPdf.mutateAsync(formData);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(blob);

      const iframe = document.querySelector('iframe');
      if (iframe?.src) {
        iframe.src = fileUrl;
        iframe.title = iframeTitle;
      }

      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      // Setting the worker for pdfjs:
      if (OS != 'linux') {
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      }

      setPdfUrl(fileUrl);
    } catch (error) {
      console.error('Failed to refresh PDF:', error);
      setHasError(true);
    }
  };

  useEffect(() => {
    if (reviewContentData.length) {
      getPDF();
    }
  }, [reviewContentData]);

  const backToOverview = () => {
    navigate(backToOverviewLink, { state: { fundId } });
  };

  useEffect(() => {
    return () => {
      dispatch(toggleControlBarShowing(false));
    };
  }, [dispatch]);

  // Toggle the control bar to be shown
  useEffect(() => {
    if (reviewContentData.length > 0) {
      dispatch(
        updateControlBarComponents([
          <BackToOverviewBtn
            backToOverview={backToOverview}
            dontSetPosition
            key="back_to_overview"
          />,
          <div className="ml-[0.62rem] mr-[0.62rem] flex max-h-[1.88rem] w-full min-w-0 items-center truncate whitespace-nowrap text-gray-800">
            <span className="truncate text-xl font-medium">{`${reviewContentData[0].comparison[0].fund_name} - ${isin}`}</span>
          </div>,
        ]),
      );
      dispatch(toggleControlBarShowing(true));
    }
  }, [reviewContentData]);

  if (isPending) {
    return <Raptor2Loading centerWrap messages={[`Loading Raidr ...`]} />;
  }

  if (error) {
    return <ErrorBoundaryMessage />;
  }

  return (
    <article className="mb-2.5 w-full overflow-hidden rounded-sm">
      <RaptorPDFViewer
        pdfUrl={pdfUrl}
        fileName={iframeTitle}
        hasError={hasError}
        errorMessage={''}
        setHasError={setHasError}
        isPending={createNewReviewKIDPdf.isPending}
      // initialMode="fitWidth"
      />
    </article>
  );
};

export default CompareKIDVersions;
